import {
    Badge, Button, ButtonProps, Card, Divider, Group, Stack, Text, Title, Tooltip,
} from '@mantine/core';
import { Checkmark, StarFilled } from '@carbon/icons-react';
import cx from 'classnames';

import { SubscriptionPlanDto } from 'api/dtos/subscriptionPlanDtos';

import styles from './SubscriptionPlanCard.module.scss';

type Props = {
    plan: SubscriptionPlanDto;
    monthLabel: string;
    actionLabel: string;
    onAction: () => void;
    onCardClick?: () => void;
    actionDisabled?: boolean;
    actionDisabledTooltip?: string;
    actionVariant?: ButtonProps['variant'];
    highlighted?: boolean;
    popular?: boolean;
    popularLabel?: string;
    maxVisibleFeatures?: number;
    className?: string;
};

const SubscriptionPlanCard = ({
    plan,
    monthLabel,
    actionLabel,
    onAction,
    onCardClick,
    actionDisabled,
    actionDisabledTooltip,
    actionVariant = 'light',
    highlighted,
    popular,
    popularLabel,
    maxVisibleFeatures = 4,
    className,
}: Props) => {
    const effectivePrice = plan.discountedMonthlyPrice ?? plan.monthlyPrice;
    const hasDiscount = plan.discountedMonthlyPrice && plan.discountedMonthlyPrice !== plan.monthlyPrice;

    const actionButton = (
        <Button
            disabled={actionDisabled}
            variant={actionVariant}
            fullWidth
            mt="md"
            className={cx(styles.actionButton, highlighted && styles.actionButtonSelected)}
            onClick={onAction}
        >
            {actionLabel}
        </Button>
    );

    return (
        <Card
            shadow="sm"
            padding="lg"
            radius="md"
            withBorder
            className={cx(
                styles.card,
                highlighted && styles.cardSelected,
                popular && styles.cardPopular,
                onCardClick && styles.cardClickable,
                className,
            )}
            onClick={onCardClick}
            role={onCardClick ? 'button' : undefined}
            tabIndex={onCardClick ? 0 : undefined}
            onKeyDown={onCardClick ? (event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    onCardClick();
                }
            } : undefined}
        >
            <Stack justify="space-between" h="100%">
                <div>
                    {popular && popularLabel && (
                        <Badge
                            variant="outline"
                            color="blue"
                            leftSection={<StarFilled size={12} />}
                            className={styles.popularBadge}
                        >
                            {popularLabel}
                        </Badge>
                    )}

                    <Title order={3} size="h4" mb="xs" className={cx(popular && styles.titleWithBadge)}>
                        {plan.name}
                    </Title>

                    <div className={styles.price}>
                        0 PLN
                        <span className={styles.priceSuffix}>
                            {' '}
                            pierwsze 14 dni
                        </span>
                    </div>
                    <div className={styles.priceAfterTrial}>
                        potem
                        {' '}
                        {hasDiscount && <s>{plan.monthlyPrice}</s>}
                        {hasDiscount && <span className={styles.discountedPrice}>{effectivePrice}</span>}
                        {!hasDiscount && effectivePrice}
                        {' zł '}
                        <span className={styles.priceSuffix}>
                            /
                            {' '}
                            {monthLabel}
                        </span>
                    </div>

                    <Divider mb="md" />

                    <Stack gap="xs" className={styles.features}>
                        {plan.features.slice(0, maxVisibleFeatures).map((feature) => (
                            <Group key={feature} gap={6} wrap="nowrap" align="flex-start">
                                <Checkmark size={16} className={styles.featureIcon} />
                                <Text size="sm">{feature}</Text>
                            </Group>
                        ))}
                    </Stack>
                </div>

                {actionDisabled && actionDisabledTooltip ? (
                    <Tooltip label={actionDisabledTooltip} withArrow>
                        <span className={styles.actionTooltipWrapper}>
                            {actionButton}
                        </span>
                    </Tooltip>
                ) : actionButton}
            </Stack>
        </Card>
    );
};

export default SubscriptionPlanCard;
